import { useState, useEffect, useRef } from 'react'

const WS_RECONNECT_DELAY_MS = 3_000

interface UseDashboardWebSocketOptions {
  onUpdate: () => void
}

interface UseDashboardWebSocketReturn {
  connected: boolean
}

/**
 * Hook that connects to the dashboard WebSocket endpoint.
 *
 * - Connects to ws://localhost:8082/ws/dashboard (or VITE_WS_URL env override)
 * - On receiving {"type":"dashboard_update"}, calls the onUpdate callback
 * - Auto-reconnects after 3 seconds on close
 * - On unmount: closes the socket and cancels any pending reconnect
 */
export function useDashboardWebSocket({
  onUpdate,
}: UseDashboardWebSocketOptions): UseDashboardWebSocketReturn {
  const [connected, setConnected] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)
  const onUpdateRef = useRef(onUpdate)

  // Keep the callback ref up to date without re-triggering the effect
  onUpdateRef.current = onUpdate

  useEffect(() => {
    isMountedRef.current = true

    function clearReconnectTimer() {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
    }

    function connect() {
      if (!isMountedRef.current) return

      // Close any existing connection cleanly
      if (wsRef.current) {
        wsRef.current.onopen = null
        wsRef.current.onclose = null
        wsRef.current.onerror = null
        wsRef.current.onmessage = null
        if (
          wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING
        ) {
          wsRef.current.close()
        }
        wsRef.current = null
      }

      const wsBase =
        import.meta.env.VITE_WS_URL || 'ws://localhost:8082'
      const url = `${wsBase}/ws/dashboard`
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        if (!isMountedRef.current) return
        setConnected(true)
      }

      ws.onmessage = (event: MessageEvent) => {
        if (!isMountedRef.current) return
        try {
          const data: unknown = JSON.parse(String(event.data))
          if (
            typeof data === 'object' &&
            data !== null &&
            (data as Record<string, unknown>).type === 'dashboard_update'
          ) {
            onUpdateRef.current()
          }
        } catch {
          // Ignore non-JSON or malformed messages
        }
      }

      ws.onclose = () => {
        if (!isMountedRef.current) return
        setConnected(false)
        // Schedule a reconnect in 3 seconds
        clearReconnectTimer()
        reconnectTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            connect()
          }
        }, WS_RECONNECT_DELAY_MS)
      }

      ws.onerror = () => {
        // onclose fires after onerror — reconnect is handled there
      }
    }

    connect()

    return () => {
      isMountedRef.current = false
      clearReconnectTimer()
      if (wsRef.current) {
        wsRef.current.onopen = null
        wsRef.current.onclose = null
        wsRef.current.onerror = null
        wsRef.current.onmessage = null
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, []) // intentionally empty — we use refs for callbacks

  return { connected }
}
